// ============================================================================
// api — twoFactorHooks
// Owner: Claude 3 (Frontend & UX).
// Purpose: TanStack Query hooks for the 2FA enrollment flows.
// URS rows: AUTH-002..006, SEC-021.
// ============================================================================
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from './client';

export type Factor = 'TOTP' | 'WEBAUTHN' | 'SMS';

export type EnrolledFactor = {
  factor: Factor;
  enrolledAt: string;
  label?: string;
};

export type TotpEnrollResponse = {
  secret: string;
  provisioningUri: string;
  qrPngBase64?: string;
};

export type WebAuthnOptionsResponse = {
  publicKey: PublicKeyCredentialCreationOptions;
};

export function useMyEnrolledFactors() {
  return useQuery<EnrolledFactor[]>({
    queryKey: ['my-2fa-factors'],
    queryFn: () => apiFetch<EnrolledFactor[]>('/api/v1/users/me/factors'),
  });
}

export function useEnrollTotp() {
  return useMutation<TotpEnrollResponse, Error, void>({
    mutationFn: () =>
      apiFetch<TotpEnrollResponse>('/api/v1/auth/2fa/totp/enroll', {
        method: 'POST',
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}

export function useVerifyTotp() {
  return useMutation<{ ok: true }, Error, { code: string }>({
    mutationFn: ({ code }) =>
      apiFetch<{ ok: true }>('/api/v1/auth/2fa/totp/verify', {
        method: 'POST',
        body: { code },
      }),
  });
}

export function useWebAuthnOptions() {
  return useMutation<WebAuthnOptionsResponse, Error, void>({
    mutationFn: () =>
      apiFetch<WebAuthnOptionsResponse>('/api/v1/auth/2fa/webauthn/options', {
        method: 'POST',
      }),
  });
}

export function useWebAuthnRegister() {
  return useMutation<{ ok: true }, Error, { attestation: unknown }>({
    mutationFn: ({ attestation }) =>
      apiFetch<{ ok: true }>('/api/v1/auth/2fa/webauthn/register', {
        method: 'POST',
        body: { attestation },
      }),
  });
}

export function useEnrollSms() {
  return useMutation<{ challengeId: string }, Error, { phoneNumber: string }>({
    mutationFn: ({ phoneNumber }) =>
      apiFetch<{ challengeId: string }>('/api/v1/auth/2fa/sms/enroll', {
        method: 'POST',
        body: { phoneNumber },
      }),
  });
}

export function useVerifySms() {
  return useMutation<{ ok: true }, Error, { challengeId: string; code: string }>({
    mutationFn: ({ challengeId, code }) =>
      apiFetch<{ ok: true }>('/api/v1/auth/2fa/sms/verify', {
        method: 'POST',
        body: { challengeId, code },
      }),
  });
}
